import type { RosterItemResponse } from '@on3/api';
import { useSite } from '@on3/ui-lib/index';
import {
  type ColumnOption,
  getColumnHeaderList,
} from '@public/components/Db/Filters/SortFilter';
import { ArrowRight } from '@public/components/Svg/ArrowRight';
import { PrimaryButton } from '@public/components/UI/Buttons/PrimaryButton';
import { Text } from '@public/components/UI/Typography/Text';
import clsx from 'clsx';
import { useCallback } from 'react';

import styles from './HighSchoolRosterHeader.module.scss';

interface IHighSchoolHeadersProps {
  selected?: keyof RosterItemResponse | string;
  onChange?: React.MouseEventHandler<HTMLButtonElement>;
  ascending?: boolean;
  primaryColor?: string | null;
}
const columns: ColumnOption[] = [
  'jerseyNumber',
  'lastName',
  'position',
  'rating',
  'nilValue',
  'status',
  'athleteVerified',
];

export const highSchoolHeaderListSelection = getColumnHeaderList(columns);
export const HighSchoolRosterHeader = ({
  selected,
  onChange,
  ascending,
  primaryColor,
}: IHighSchoolHeadersProps) => {
  const { currentSite } = useSite();
  const themeColor = primaryColor || currentSite?.primaryColor || '#fe3b1f';
  const buttonStyles = useCallback(
    (isActive: boolean) => ({ borderColor: isActive ? themeColor : '#e4e8f2' }),
    [themeColor],
  );

  const handleClick: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    (e) => {
      onChange?.(e);
    },
    [onChange],
  );

  return (
    <header>
      <ul className={styles.headerListContainer}>
        {highSchoolHeaderListSelection.map((item) => {
          const isSelected = item.name === selected;

          return (
            <li className={styles.listItem} key={item.key}>
              <PrimaryButton
                className={styles.btnSort}
                data-flip={item.flipDirection}
                data-sorttype={item.name}
                endIcon={
                  selected === item.name && (
                    <ArrowRight
                      className={clsx(styles.arrow, {
                        [styles.flip]: item.flipDirection
                          ? !ascending
                          : ascending,
                      })}
                      stroke={themeColor}
                    />
                  )
                }
                onClick={handleClick}
                style={buttonStyles(isSelected)}
                variant="text"
              >
                <Text
                  className={styles.title}
                  component="h4"
                  variant="subtitle2"
                >
                  {item.displayName}
                </Text>
              </PrimaryButton>
            </li>
          );
        })}
      </ul>
    </header>
  );
};
